import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Typography,
  Stack,
  Skeleton,
  alpha,
  useTheme,
  IconButton,
  MenuItem,
  Select,
  Button,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { motion } from 'framer-motion';
import CircleIcon from '@mui/icons-material/Circle';
import OpacityIcon from '@mui/icons-material/Opacity';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AddIcon from '@mui/icons-material/Add';
import LayersIcon from '@mui/icons-material/Layers';
import EvStationIcon from '@mui/icons-material/EvStation';
import SyncIcon from '@mui/icons-material/Sync';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { APIService } from 'src/api/api';
import { getAllSites } from 'src/redux/siteSlice';
import ROUTE from 'src/utils/routeConstants';
import {
  getFormattedDateTime12Hour,
  getFuelQuantity,
  getLocalData
} from 'src/utils/getInitials';
import { STORAGE_KEY } from 'src/utils/constants';

// --- ANIMATION CONFIG ---
const liquidTransition = {
  duration: 2,
  ease: 'easeInOut'
};

let svgIdCounter = 0;
const TANK_ORDER_STORAGE_KEY = 'dashboard_fuel_analytics_tank_order';
const DRAG_SCROLL_EDGE_SIZE = 72;
const DRAG_SCROLL_MAX_STEP = 18;
const US_GALLONS_PER_LITRE = 0.264172;

const useStableSvgId = (prefix) => {
  const idRef = useRef('');
  if (!idRef.current) {
    svgIdCounter += 1;
    idRef.current = `${prefix}-${svgIdCounter}`;
  }
  return idRef.current;
};

const formatLastSynced = (value) => {
  const formattedDateTime = getFormattedDateTime12Hour(value);
  return formattedDateTime || null;
};

const getSafeStockGallons = (displayedStock, configuredValue) => {
  const suppliedGallons = Number(configuredValue);
  return Number.isFinite(suppliedGallons) && suppliedGallons >= 0
    ? suppliedGallons
    : displayedStock * US_GALLONS_PER_LITRE;
};

const formatStockValue = (value, configuredValue) => {
  const stock = Number(value) || 0;
  const displayedStock = Math.max(0, stock);
  const gallons = getSafeStockGallons(displayedStock, configuredValue);
  const formattedValue = getFuelQuantity(displayedStock, gallons);
  if (configuredValue != null || getLocalData(STORAGE_KEY.user, true)?.fuel_volume_unit) {
    return formattedValue;
  }
  if (Math.abs(displayedStock) >= 1000) {
    return `${(displayedStock / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} KL`;
  }
  return `${displayedStock.toLocaleString()} L`;
};

const normalizeMatchValue = (value) => String(value || '').trim().toLowerCase();

const getTankReconciliation = (tank, reports = []) => {
  const tankName = normalizeMatchValue(tank?.name || tank?.tank_name);
  const tankSite = normalizeMatchValue(
    tank?.site_name || tank?.site?.name || tank?.site?.site_name
  );

  return reports.find((report) => {
    const reportName = normalizeMatchValue(report?.dispenser_tank_name || report?.tank_name);
    const reportSite = normalizeMatchValue(report?.site_name || report?.site?.name);
    const namesMatch = tankName && reportName && tankName === reportName;
    const sitesMatch = !tankSite || !reportSite || tankSite === reportSite;
    return namesMatch && sitesMatch;
  }) || null;
};

const isTankActive = (tank) => {
  const balance = tank?.currentBalance ?? tank?.current_balance;
  return tank?.fuel_level_only === true || balance?.dispenser_status === true;
};

const sortTanksByActiveFirst = (tankList = []) => (
  [...tankList].sort((firstTank, secondTank) => Number(isTankActive(secondTank)) - Number(isTankActive(firstTank)))
);

const getTankIdentifier = (tank) => {
  const identifier = tank?.id ?? tank?.uuid ?? tank?.name;
  return identifier === undefined || identifier === null ? '' : String(identifier);
};

const getTankOrderStorageKey = () => {
  const user = getLocalData(STORAGE_KEY.user, true);
  const userIdentifier = user?.id
    ?? user?.uuid
    ?? user?.username
    ?? user?.email
    ?? user?.name
    ?? 'default';
  return `${TANK_ORDER_STORAGE_KEY}:${userIdentifier}`;
};

const readTankOrder = () => {
  try {
    const rawOrder = window.localStorage.getItem(getTankOrderStorageKey());
    const parsedOrder = rawOrder ? JSON.parse(rawOrder) : [];
    return Array.isArray(parsedOrder) ? parsedOrder.filter(Boolean).map(String) : [];
  } catch (error) {
    console.warn('Unable to read tank order preference', error);
    return [];
  }
};

const storeTankOrder = (order) => {
  try {
    window.localStorage.setItem(getTankOrderStorageKey(), JSON.stringify(order));
  } catch (error) {
    console.warn('Unable to save tank order preference', error);
  }
};

const applyTankOrder = (tankList = [], order = []) => {
  if (!order.length) return tankList;

  const orderedIndexes = new Map(order.map((tankKey, index) => [tankKey, index]));
  return [...tankList].sort((firstTank, secondTank) => {
    const firstKey = getTankIdentifier(firstTank);
    const secondKey = getTankIdentifier(secondTank);
    const firstIndex = orderedIndexes.has(firstKey) ? orderedIndexes.get(firstKey) : Number.MAX_SAFE_INTEGER;
    const secondIndex = orderedIndexes.has(secondKey) ? orderedIndexes.get(secondKey) : Number.MAX_SAFE_INTEGER;

    return firstIndex - secondIndex;
  });
};

// --- SVG SHAPES ---

const FuelDispenserShape = ({ percentage, color }) => {
  const svgId = useStableSvgId('pump');
  const gradientId = `pump-grad-${svgId}`;
  const clipId = `pump-fill-clip-${svgId}`;
  // Petrol Pump / Fuel Dispenser (For Non-Movable Tanks)
  // ViewBox: 0 0 100 100

  const bodyHeight = 70;
  const liquidYStart = 95;
  const fillHeight = (percentage / 100) * bodyHeight;
  const liquidY = liquidYStart - fillHeight;

  return (
    <svg width="70" height="90" viewBox="0 0 100 100">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={color} stopOpacity={0.8} />
          <stop offset="50%" stopColor={color} stopOpacity={0.6} />
          <stop offset="100%" stopColor={color} stopOpacity={0.8} />
        </linearGradient>
        <clipPath id={clipId}>
          <rect x="25" y="25" width="50" height="70" rx="2" />
        </clipPath>
      </defs>

      {/* Hose (Behind) */}
      <path
        d="M75,45 C90,45 90,80 75,80"
        fill="none"
        stroke="#1F2937"
        strokeWidth="3"
        strokeLinecap="round"
      />

      {/* Main Cabinet Background */}
      <rect
        x="25"
        y="25"
        width="50"
        height="70"
        rx="2"
        fill="#F3F4F6"
        stroke="#4B5563"
        strokeWidth="2"
      />

      {/* Liquid Animation (Inside Cabinet) */}
      <g clipPath={`url(#${clipId})`}>
        <motion.rect
          x="25"
          y="25"
          width="50"
          height="70"
          fill={`url(#${gradientId})`}
          initial={{ attrY: 95, attrHeight: 0 }}
          animate={{ attrY: liquidY, attrHeight: fillHeight }}
          transition={liquidTransition}
        />
      </g>

      {/* Footer / Base */}
      <rect x="20" y="92" width="60" height="6" fill="#374151" rx="1" />

      {/* Header / Canopy */}
      <path
        d="M20,5 H80 A2,2 0 0 1 82,7 V25 H18 V7 A2,2 0 0 1 20,5 Z"
        fill="#1F2937"
      />
      {/* Brand Stripe */}
      <rect x="18" y="20" width="64" height="5" fill={color} />

      {/* Screen / Display Area */}
      <rect x="30" y="32" width="40" height="20" fill="#111827" rx="1" />
      <rect x="32" y="34" width="36" height="16" fill="#1F2937" />
      <path d="M35,42 h25" stroke="#10B981" strokeWidth="2" strokeDasharray="2 2" opacity="0.5" />

      {/* Keypad / Buttons Area */}
      <rect x="35" y="58" width="30" height="25" fill="#E5E7EB" rx="1" opacity="0.5" />
      <circle cx="40" cy="63" r="1.5" fill="#6B7280" />
      <circle cx="50" cy="63" r="1.5" fill="#6B7280" />
      <circle cx="60" cy="63" r="1.5" fill="#6B7280" />
      <circle cx="40" cy="70" r="1.5" fill="#6B7280" />
      <circle cx="50" cy="70" r="1.5" fill="#6B7280" />
      <circle cx="60" cy="70" r="1.5" fill="#6B7280" />

      {/* Nozzle Holster & Handle */}
      <rect x="73" y="35" width="6" height="15" fill="#374151" rx="1" />
      <path
        d="M76,35 L82,35 L82,45 L76,40"
        fill="#111827"
      />

      {/* Glass/Gloss Highlight */}
      <path
        d="M25,25 H75 V50 L25,35 Z"
        fill="white"
        fillOpacity="0.1"
        className="csp-pointer-events-none"
      />
    </svg>
  );
};

FuelDispenserShape.propTypes = {
  percentage: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired
};

const UndergroundTankShape = ({ percentage, color }) => {
  const svgId = useStableSvgId('ust');
  const gradientId = `ust-grad-${svgId}`;
  const soilPatternId = `soil-pattern-${svgId}`;
  const clipId = `ust-fill-clip-${svgId}`;
  // Underground Tank (Buried Horizontal Capsule)
  // ViewBox: 0 0 120 90

  const tankHeight = 45;
  const tankBottomY = 35 + tankHeight;
  const fillHeight = (percentage / 100) * tankHeight;
  const liquidY = tankBottomY - fillHeight;

  return (
    <svg width="100" height="75" viewBox="0 0 120 90">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={0.6} />
          <stop offset="100%" stopColor={color} stopOpacity={0.9} />
        </linearGradient>
        <pattern id={soilPatternId} x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1" fill="#9CA3AF" opacity="0.3" />
          <circle cx="7" cy="7" r="1" fill="#9CA3AF" opacity="0.3" />
        </pattern>
        <clipPath id={clipId}>
          <rect x="10" y="35" width="100" height="45" rx="22.5" />
        </clipPath>
      </defs>

      {/* Ground Level */}
      <rect x="0" y="20" width="120" height="5" fill="#4B5563" />
      <rect x="0" y="25" width="120" height="65" fill={`url(#${soilPatternId})`} opacity="0.1" />

      {/* Fill Pipe */}
      <path d="M30,20 V35" stroke="#6B7280" strokeWidth="4" />
      <rect x="27" y="15" width="6" height="5" fill="#374151" rx="1" />

      {/* Vent Pipe */}
      <path d="M90,10 V35" stroke="#6B7280" strokeWidth="3" />
      <path d="M90,10 L95,10" stroke="#6B7280" strokeWidth="3" strokeLinecap="round" />

      {/* Tank Body Shell */}
      <rect
        x="10"
        y="35"
        width="100"
        height="45"
        rx="22.5"
        fill="#F3F4F6"
        stroke="#6B7280"
        strokeWidth="2"
      />

      {/* Liquid Animation */}
      <g clipPath={`url(#${clipId})`}>
        <motion.rect
          x="10"
          y="35"
          width="100"
          height="45"
          fill={`url(#${gradientId})`}
          initial={{ attrY: 80, attrHeight: 0 }}
          animate={{ attrY: liquidY, attrHeight: fillHeight }}
          transition={liquidTransition}
        />
        <motion.path
          d="M10,80 H110"
          stroke="white"
          strokeOpacity="0.6"
          strokeWidth="1.5"
          animate={{ d: `M10,${liquidY} H110` }}
          transition={liquidTransition}
        />
      </g>

      {/* Measurement stick line */}
      <line x1="60" y1="20" x2="60" y2="50" stroke="#9CA3AF" strokeWidth="1" strokeDasharray="2 2" />

      {/* Gloss Highlight */}
      <path
        d="M20,38 H100 A10,10 0 0 1 100,45 H20 A10,10 0 0 1 20,38 Z"
        fill="white"
        fillOpacity="0.15"
      />
    </svg>
  );
};

UndergroundTankShape.propTypes = {
  percentage: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired
};

const AboveGroundTankShape = ({ percentage, color }) => {
  const svgId = useStableSvgId('agt');
  const gradientId = `agt-grad-${svgId}`;
  const clipId = `agt-fill-clip-${svgId}`;
  const tankHeight = 45;
  const tankBottomY = 45 + tankHeight;
  const fillHeight = (percentage / 100) * tankHeight;
  const liquidY = tankBottomY - fillHeight;

  return (
    <svg width="100" height="85" viewBox="0 0 120 100">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={0.6} />
          <stop offset="100%" stopColor={color} stopOpacity={0.9} />
        </linearGradient>
        <clipPath id={clipId}>
          <rect x="10" y="45" width="100" height="45" rx="22.5" />
        </clipPath>
      </defs>

      {/* Tank supports */}
      <rect x="25" y="90" width="8" height="6" fill="#6B7280" rx="1" />
      <rect x="87" y="90" width="8" height="6" fill="#6B7280" rx="1" />

      {/* Ground line below tank */}
      <rect x="0" y="96" width="120" height="3" fill="#4B5563" />

      {/* Fill and vent pipes above tank */}
      <path d="M30,30 V45" stroke="#6B7280" strokeWidth="4" />
      <rect x="27" y="25" width="6" height="5" fill="#374151" rx="1" />
      <path d="M90,20 V45" stroke="#6B7280" strokeWidth="3" />
      <path d="M90,20 L95,20" stroke="#6B7280" strokeWidth="3" strokeLinecap="round" />

      {/* Tank body */}
      <rect
        x="10"
        y="45"
        width="100"
        height="45"
        rx="22.5"
        fill="#F3F4F6"
        stroke="#6B7280"
        strokeWidth="2"
      />

      {/* Liquid fill animation (bottom to top) */}
      <g clipPath={`url(#${clipId})`}>
        <motion.rect
          x="10"
          y="45"
          width="100"
          height="45"
          fill={`url(#${gradientId})`}
          initial={{ attrY: 90, attrHeight: 0 }}
          animate={{ attrY: liquidY, attrHeight: fillHeight }}
          transition={liquidTransition}
        />
        <motion.path
          d="M10,90 H110"
          stroke="white"
          strokeOpacity="0.6"
          strokeWidth="1.5"
          animate={{ d: `M10,${liquidY} H110` }}
          transition={liquidTransition}
        />
      </g>

      {/* Gloss highlight */}
      <path
        d="M20,48 H100 A10,10 0 0 1 100,55 H20 A10,10 0 0 1 20,48 Z"
        fill="white"
        fillOpacity="0.15"
      />
    </svg>
  );
};

AboveGroundTankShape.propTypes = {
  percentage: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired
};

const MovableTankShape = ({ percentage, color }) => {
  const svgId = useStableSvgId('truck');
  const gradientId = `truck-grad-${svgId}`;
  const clipId = `truck-fill-clip-${svgId}`;
  const tankHeight = 36;
  const tankBottomY = 15 + tankHeight;
  const fillHeight = (percentage / 100) * tankHeight;
  const liquidY = tankBottomY - fillHeight;

  return (
    <svg width="110" height="65" viewBox="0 0 140 80">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={0.6} />
          <stop offset="50%" stopColor={color} stopOpacity={1} />
          <stop offset="100%" stopColor={color} stopOpacity={0.8} />
        </linearGradient>
        <clipPath id={clipId}>
          <rect x="5" y="15" width="85" height="36" rx="12" />
        </clipPath>
      </defs>

      {/* Chassis */}
      <rect x="5" y="52" width="125" height="6" fill="#374151" rx="2" />
      <rect x="90" y="40" width="10" height="5" fill="#6B7280" />

      {/* Cabin */}
      <path
        d="M100,25 H125 A5,5 0 0 1 130,30 V52 H100 V25 Z"
        fill="#1F2937"
      />
      <path d="M102,28 H125 V40 H102 Z" fill="#60A5FA" opacity="0.6" />
      <rect x="128" y="48" width="4" height="8" fill="#9CA3AF" />

      {/* Tank Container */}
      <rect
        x="5"
        y="15"
        width="85"
        height="36"
        rx="12"
        fill="#F3F4F6"
        stroke="#9CA3AF"
        strokeWidth="2"
      />
      <rect x="25" y="15" width="4" height="36" fill="#D1D5DB" />
      <rect x="65" y="15" width="4" height="36" fill="#D1D5DB" />

      {/* Liquid Animation */}
      <g clipPath={`url(#${clipId})`}>
        <motion.rect
          x="5"
          y="15"
          width="85"
          height="36"
          fill={`url(#${gradientId})`}
          initial={{ attrY: tankBottomY, attrHeight: 0 }}
          animate={{ attrY: liquidY, attrHeight: fillHeight }}
          transition={liquidTransition}
        />
        <motion.path
          d={`M5,${tankBottomY} H90`}
          stroke="white"
          strokeOpacity="0.5"
          strokeWidth="2"
          animate={{ d: `M5,${liquidY} H90` }}
          transition={liquidTransition}
        />
      </g>

      {/* Highlight */}
      <path
        d="M10,20 H80 A5,5 0 0 1 80,25 H10 A5,5 0 0 1 10,20 Z"
        fill="white"
        fillOpacity="0.2"
      />

      {/* Wheels */}
      <g transform="translate(20, 58)">
        <circle cx="0" cy="0" r="10" fill="#111827" />
        <circle cx="0" cy="0" r="4" fill="#9CA3AF" />
      </g>
      <g transform="translate(45, 58)">
        <circle cx="0" cy="0" r="10" fill="#111827" />
        <circle cx="0" cy="0" r="4" fill="#9CA3AF" />
      </g>
      <g transform="translate(115, 58)">
        <circle cx="0" cy="0" r="10" fill="#111827" />
        <circle cx="0" cy="0" r="4" fill="#9CA3AF" />
      </g>
    </svg>
  );
};

MovableTankShape.propTypes = {
  percentage: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired
};

// --- Single Tank Card Component ---

const TankReconciliationTooltip = ({
  reconciliation,
  currentStock,
  currentStockGallons,
  children
}) => (
  <Tooltip
    arrow
    placement="top"
    slotProps={{
      tooltip: {
        sx: {
          bgcolor: '#FFFFFF',
          color: '#1F2937',
          border: '1px solid #E5E7EB',
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
          p: 1.25,
          '& .MuiTooltip-arrow': {
            color: '#FFFFFF',
            '&::before': {
              border: '1px solid #E5E7EB'
            }
          }
        }
      }
    }}
    title={(
      <Stack spacing={0.7} sx={{ minWidth: 190 }}>
        {[
          ['Opening Stock', reconciliation?.opening_value, reconciliation?.opening_value_gallons],
          ['Inward', reconciliation?.inward, reconciliation?.inward_gallons],
          ['Transfer', reconciliation?.transfer, reconciliation?.transfer_gallons],
          ['Issued', reconciliation?.sales, reconciliation?.sales_gallons],
          ['Closing Stock', currentStock, currentStockGallons]
        ].map(([label, value, configuredValue]) => (
          <Stack
            key={label}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Typography
              variant="caption"
              sx={{ color: '#6B7280', fontSize: '0.7rem' }}
            >
              {label}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#111827', fontWeight: 700, fontSize: '0.72rem' }}
            >
              {reconciliation || label === 'Closing Stock'
                ? getFuelQuantity(Number(value) || 0, configuredValue)
                : 'N/A'}
            </Typography>
          </Stack>
        ))}
      </Stack>
    )}
  >
    <Box component="span" sx={{ display: 'block' }}>
      {children}
    </Box>
  </Tooltip>
);

TankReconciliationTooltip.propTypes = {
  reconciliation: PropTypes.object,
  currentStock: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  currentStockGallons: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node.isRequired
};

const TankCard = ({ tank }) => {
  const theme = useTheme();
  const history = useHistory();
  const {
    id,
    name,
    currentBalance,
    maxVolume,
    productName,
    type,
  } = {
    ...tank,
    currentBalance: tank.currentBalance ?? tank.current_balance,
    maxVolume: tank.maxVolume ?? tank.max_volume,
    productName: tank.productName ?? tank.product_name
  };
  const isFuelLevelOnly = tank.fuel_level_only === true;
  const isDispenserDisabled = !isFuelLevelOnly && currentBalance?.dispenser_status !== true;

  const stock = Number(currentBalance?.stock) || 0;
  const status = isFuelLevelOnly ? 'Online' : (currentBalance?.status || 'Offline');
  const isOnline = status === 'Online';
  const lastSyncedValue = tank.last_synced
    || tank.lastSynced
    || currentBalance?.last_synced
    || currentBalance?.lastSynced;
  const lastSyncedText = formatLastSynced(lastSyncedValue);
  const tankType = type?.toLowerCase() || 'tank';
  const isMovable = tankType === 'movable';
  const isUnderground = tankType === 'underground';
  const isNonMovable = tankType === 'non-movable'
    || tankType === 'non movable'
    || tankType === 'nonmovable'
    || tankType === 'non_movable';

  // Calculations
  const maxCapValue = Number(maxVolume) || 0;
  const maxCap = maxCapValue > 0 ? maxCapValue : 0;
  let percentage = 0;
  if (maxCap > 0) {
    percentage = (stock / maxCap) * 100;
  } else if (stock > 0) {
    percentage = 100;
  }
  percentage = Math.min(100, Math.max(0, percentage));

  // --- Theme Config ---
  let levelConfig = {
    label: 'Healthy',
    color: theme.palette.success.main,
    icon: null
  };

  if (percentage <= 20) {
    levelConfig = {
      label: 'Low Stock',
      color: theme.palette.error.main,
      icon: null
    };
  } else if (percentage <= 75) {
    levelConfig = {
      label: 'Adequate',
      color: theme.palette.warning.main,
      icon: null
    };
  }

  if (percentage <= 20) {
    levelConfig.icon = <WarningAmberIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />;
  } else if (percentage <= 75) {
    levelConfig.icon = <InfoIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />;
  } else {
    levelConfig.icon = <CheckCircleIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />;
  }

  let waveColor = '#3B82F6'; // Default Blue
  if (productName?.toLowerCase().includes('diesel')) waveColor = '#F59E0B'; // Amber/Orange
  if (productName?.toLowerCase().includes('petrol')) waveColor = '#2563EB';
  if (percentage <= 20) waveColor = theme.palette.error.main;

  // Determine Icon
  let TypeIcon = EvStationIcon; // Default to pump icon for static
  if (isMovable) TypeIcon = LocalShippingIcon;
  if (isUnderground || isNonMovable) TypeIcon = LayersIcon;

  // Determine Visual Component
  let TankVisualComponent = FuelDispenserShape;
  if (isMovable) {
    TankVisualComponent = MovableTankShape;
  } else if (isUnderground) {
    TankVisualComponent = UndergroundTankShape;
  } else if (isNonMovable) {
    TankVisualComponent = AboveGroundTankShape;
  }

  const handleCardClick = () => {
    if (id !== undefined && id !== null) {
      history.push(`${ROUTE.tanks}/${id}`);
    }
  };

  return (
    <Card
      onClick={handleCardClick}
      role={id !== undefined && id !== null ? 'button' : undefined}
      tabIndex={id !== undefined && id !== null ? 0 : -1}
      onKeyDown={(event) => {
        if ((event.key === 'Enter' || event.key === ' ') && id !== undefined && id !== null) {
          event.preventDefault();
          handleCardClick();
        }
      }}
      sx={{
        width: '100%',
        p: 2,
        borderRadius: 3,
        filter: isDispenserDisabled ? 'blur(0.5px)' : 'none',
        pointerEvents: isDispenserDisabled ? 'none' : 'auto',
        position: 'relative',
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.1),
        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',
        transition: 'all 0.3s ease',
        cursor: id !== undefined && id !== null ? 'pointer' : 'default',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 16px -4px ${alpha(waveColor, 0.15)}`,
          borderColor: alpha(waveColor, 0.3)
        }
      }}
    >
      {/* Top Row: Name & Status */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1.5}
        sx={{
          mb: 1.5,
          minWidth: 0
        }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              lineHeight: 1.3,
              mb: 0.5,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {(name || 'N/A').toUpperCase()}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <TypeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 500,
                textTransform: 'capitalize'
              }}
            >
              {type || 'Tank'}
            </Typography>
          </Stack>
        </Box>

        <Stack alignItems="flex-end" spacing={0.4}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: isOnline ? alpha('#10B981', 0.1) : alpha(theme.palette.text.disabled, 0.1),
              px: 1,
              py: 0.25,
              borderRadius: 10
            }}
          >
            <CircleIcon
              sx={{
                fontSize: 6,
                color: isOnline ? '#10B981' : theme.palette.text.disabled,
                animation: isOnline ? 'pulse 1.5s infinite' : 'none'
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: isOnline ? '#065F46' : theme.palette.text.secondary,
                fontSize: '0.65rem',
                textTransform: 'uppercase'
              }}
            >
              {status}
            </Typography>
          </Box>
          {lastSyncedText && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                color: 'text.secondary'
              }}
            >
              Last synced:
              {' '}
              {lastSyncedText}
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* Middle Row: Visual & Metrics */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(92px, 38%) minmax(0, 1fr)',
          alignItems: 'center',
          gap: 1.5,
          '@media (max-width: 330px)': {
            gridTemplateColumns: '1fr',
            gap: 1
          }
        }}
      >
        {/* Visual Component */}
        <Box
          sx={{
            minWidth: 0,
            pt: 1,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <TankVisualComponent percentage={percentage} color={waveColor} />
        </Box>

        {/* Info Column */}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              display: 'block',
              mb: 0.25
            }}
          >
            Capacity
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              lineHeight: 1.1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {getFuelQuantity(
              Math.max(0, stock),
              getSafeStockGallons(Math.max(0, stock), currentBalance?.stock_gallons)
            )}
            <Typography
              component="span"
              variant="caption"
              sx={{ fontWeight: 600, color: 'text.secondary', ml: 0.5 }}
            >
              /
              {' '}
              {maxCap > 0
                ? getFuelQuantity(maxCap, tank.max_volume_gallons)
                : '--'}
            </Typography>
          </Typography>

          {/* Progress Bar for context */}
          <Box
            sx={{
              width: '100%',
              height: 4,
              bgcolor: alpha(theme.palette.divider, 0.1),
              borderRadius: 2,
              mt: 1,
              mb: 1,
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                width: `${percentage}%`,
                height: '100%',
                bgcolor: waveColor,
                borderRadius: 2
              }}
            />
          </Box>

          {/* Footer Badge */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                bgcolor: alpha(waveColor, 0.1),
                color: waveColor,
                px: 0.8,
                py: 0.2,
                borderRadius: 0.5,
                fontWeight: 700,
                fontSize: '0.7rem'
              }}
            >
              {Math.round(percentage)}
              %
            </Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {levelConfig.icon}
              <Typography
                variant="caption"
                sx={{
                  color: levelConfig.color,
                  fontWeight: 600,
                  fontSize: '0.7rem'
                }}
              >
                {levelConfig.label}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>
      {isDispenserDisabled && (
      <>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 3,
            zIndex: 5
          }}
        >
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: '#000',
              letterSpacing: 0.5,
            }}
          >
            Deactivated
          </Typography>
        </Box>
      </>
      )}
    </Card>
  );
};

TankCard.propTypes = {
  tank: PropTypes.object.isRequired
};

// --- Main Stock Info Component ---

const StockInfo = ({ compact }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { allSites = [] } = useSelector((state) => state.sites);
  const [tanks, setTanks] = useState([]);
  const [totalStock, setTotalStock] = useState(0);
  const [totalStockGallons, setTotalStockGallons] = useState(null);
  const [selectedSite, setSelectedSite] = useState('all');
  const [reconciliationReports, setReconciliationReports] = useState([]);
  const [tankOrder, setTankOrder] = useState(() => readTankOrder());
  const [draggedTankKey, setDraggedTankKey] = useState('');
  const [dragOverTankKey, setDragOverTankKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const requestIdRef = useRef(0);
  const suppressNextClickRef = useRef(false);
  const scrollContainerRef = useRef(null);
  const dragScrollFrameRef = useRef(0);
  const dragScrollDeltaRef = useRef({ x: 0, y: 0 });
  const LIMIT = 5;

  const siteOptions = useMemo(() => [
    { id: 'all', name: 'All Sites' },
    ...allSites
  ], [allSites]);

  const getTankRequestParams = useCallback(() => (
    selectedSite === 'all' ? {} : { site: selectedSite }
  ), [selectedSite]);

  const fetchTotalStock = useCallback(async (params, requestId) => {
    const stockLimit = 1000;
    let pageNo = 1;
    let allMatchingTanks = [];
    let hasNextPage = true;

    while (hasNextPage) {
      // The next page URL is only known after the previous response.
      // eslint-disable-next-line no-await-in-loop
      const response = await APIService.getTankList(stockLimit, pageNo, params);
      if (!response.success) throw response;

      const result = response.result || {};
      allMatchingTanks = [...allMatchingTanks, ...(result.results || [])];
      hasNextPage = Boolean(result.next);
      pageNo += 1;
    }

    if (requestId === requestIdRef.current) {
      const stock = allMatchingTanks.reduce((sum, tank) => {
        if (tank.fuel_level_only === true) return sum;

        const balance = tank.currentBalance ?? tank.current_balance;
        const value = Number(balance?.stock);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0);
      const stockGallons = allMatchingTanks.reduce((sum, tank) => {
        if (tank.fuel_level_only === true) return sum;

        const balance = tank.currentBalance ?? tank.current_balance;
        const value = Number(balance?.stock_gallons);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0);
      setTotalStock(stock);
      setTotalStockGallons(allMatchingTanks.some((tank) => {
        const balance = tank.currentBalance ?? tank.current_balance;
        return balance?.stock_gallons != null;
      }) ? stockGallons : null);
    }
  }, []);

  const refreshTotalStock = useCallback((params, requestId) => {
    fetchTotalStock(params, requestId).catch((error) => {
      if (requestId === requestIdRef.current) {
        setTotalStock(0);
        setTotalStockGallons(null);
      }
      console.error('StockInfo total stock error:', error);
    });
  }, [fetchTotalStock]);

  const fetchTanks = useCallback(async (pageNo = 1, append = false) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const params = getTankRequestParams();

    try {
      if (append) setLoadingMore(true);
      else setLoading(true);

      const res = await APIService.getTankList(LIMIT, pageNo, params);

      if (res.success && requestId === requestIdRef.current) {
        const newResults = res.result?.results || [];
        setTanks((prev) => sortTanksByActiveFirst(append ? [...prev, ...newResults] : newResults));
        setHasMore(!!res.result?.next);
        setPage(pageNo);
        if (!append) refreshTotalStock(params, requestId);
      }
    } catch (error) {
      console.error('StockInfo error:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [getTankRequestParams, refreshTotalStock]);

  useEffect(() => {
    dispatch(getAllSites());
  }, [dispatch]);

  useEffect(() => {
    setTanks([]);
    setTotalStock(0);
    setTotalStockGallons(null);
    setPage(1);
    setHasMore(false);
    fetchTanks(1, false);
  }, [fetchTanks]);

  useEffect(() => {
    let isMounted = true;

    const fetchReconciliationReports = async () => {
      try {
        const res = await APIService.getLatestReconciliationData();
        if (!isMounted || !res?.success) return;

        const reports = res.result?.results || res.result || [];
        setReconciliationReports(Array.isArray(reports) ? reports : []);
      } catch (error) {
        if (isMounted) setReconciliationReports([]);
        console.error('StockInfo reconciliation error:', error);
      }
    };

    fetchReconciliationReports();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleLoadMore = () => {
    fetchTanks(page + 1, true);
  };

  const handleSiteChange = (event) => {
    setSelectedSite(event.target.value);
  };

  const renderHeaderActions = () => (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        width: 'auto',
        minWidth: 0,
        justifyContent: 'flex-end',
        flex: '0 0 auto'
      }}
    >
      <Select
        name="site"
        variant="outlined"
        size="small"
        value={selectedSite}
        onChange={handleSiteChange}
        sx={{
          width: 132,
          minWidth: 0,
          maxWidth: '100%',
          height: 36,
          bgcolor: '#F9FAFB',
          borderRadius: 2,
          fontSize: '0.813rem',
          fontWeight: 600,
          flex: '1 1 auto',
          '& .MuiSelect-select': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            py: 0.75,
            px: 1.25
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0,0,0,0.08)'
          }
        }}
      >
        {siteOptions.map((site) => (
          <MenuItem value={site.id} key={site.id}>
            {site.name}
          </MenuItem>
        ))}
      </Select>
      <Tooltip title="Refresh Data">
        <IconButton
          onClick={() => fetchTanks(1, false)}
          size="small"
          disabled={loading || loadingMore}
          sx={{
            bgcolor: '#F9FAFB',
            border: '1px solid',
            borderColor: 'rgba(0,0,0,0.08)',
            borderRadius: 2,
            width: 36,
            height: 36,
            color: 'text.secondary',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              borderColor: 'primary.main'
            }
          }}
        >
          <SyncIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const renderStockSummary = () => (
    <Typography
      variant="caption"
      component="div"
      sx={{
        color: theme.palette.success.main,
        whiteSpace: 'normal',
        overflowWrap: 'break-word',
        lineHeight: 1.35
      }}
    >
      <span>Total Stock: </span>
      {(() => {
        const formattedValue = formatStockValue(totalStock, totalStockGallons);
        const conversionStart = formattedValue.indexOf(' (');
        if (conversionStart === -1) {
          return <span style={{ fontWeight: 600 }}>{formattedValue}</span>;
        }
        return (
          <>
            <span style={{ fontWeight: 600 }}>
              {formattedValue.slice(0, conversionStart)}
            </span>
            <span style={{ fontWeight: 400 }}>
              {formattedValue.slice(conversionStart).replace('gallons', 'gal')}
            </span>
          </>
        );
      })()}
    </Typography>
  );

  const orderedTanks = useMemo(() => applyTankOrder(tanks, tankOrder), [tankOrder, tanks]);

  const stopDragAutoScroll = useCallback(() => {
    if (dragScrollFrameRef.current) {
      window.cancelAnimationFrame(dragScrollFrameRef.current);
      dragScrollFrameRef.current = 0;
    }
    dragScrollDeltaRef.current = { x: 0, y: 0 };
  }, []);

  const suppressClickAfterDrag = useCallback(() => {
    suppressNextClickRef.current = true;
    window.setTimeout(() => {
      suppressNextClickRef.current = false;
    }, 150);
  }, []);

  const runDragAutoScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    const { x, y } = dragScrollDeltaRef.current;

    if (!scrollContainer || (!x && !y)) {
      stopDragAutoScroll();
      return;
    }

    scrollContainer.scrollBy({ left: x, top: y });
    dragScrollFrameRef.current = window.requestAnimationFrame(runDragAutoScroll);
  }, [stopDragAutoScroll]);

  const handleDragAutoScroll = useCallback((event) => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const bounds = scrollContainer.getBoundingClientRect();
    const topDistance = event.clientY - bounds.top;
    const bottomDistance = bounds.bottom - event.clientY;
    const leftDistance = event.clientX - bounds.left;
    const rightDistance = bounds.right - event.clientX;
    let y = 0;
    let x = 0;

    if (topDistance < DRAG_SCROLL_EDGE_SIZE && scrollContainer.scrollTop > 0) {
      y = -Math.ceil(((DRAG_SCROLL_EDGE_SIZE - topDistance) / DRAG_SCROLL_EDGE_SIZE) * DRAG_SCROLL_MAX_STEP);
    } else if (
      bottomDistance < DRAG_SCROLL_EDGE_SIZE
      && scrollContainer.scrollTop + scrollContainer.clientHeight < scrollContainer.scrollHeight
    ) {
      y = Math.ceil(((DRAG_SCROLL_EDGE_SIZE - bottomDistance) / DRAG_SCROLL_EDGE_SIZE) * DRAG_SCROLL_MAX_STEP);
    }

    if (leftDistance < DRAG_SCROLL_EDGE_SIZE && scrollContainer.scrollLeft > 0) {
      x = -Math.ceil(((DRAG_SCROLL_EDGE_SIZE - leftDistance) / DRAG_SCROLL_EDGE_SIZE) * DRAG_SCROLL_MAX_STEP);
    } else if (
      rightDistance < DRAG_SCROLL_EDGE_SIZE
      && scrollContainer.scrollLeft + scrollContainer.clientWidth < scrollContainer.scrollWidth
    ) {
      x = Math.ceil(((DRAG_SCROLL_EDGE_SIZE - rightDistance) / DRAG_SCROLL_EDGE_SIZE) * DRAG_SCROLL_MAX_STEP);
    }

    dragScrollDeltaRef.current = { x, y };
    if ((x || y) && !dragScrollFrameRef.current) {
      dragScrollFrameRef.current = window.requestAnimationFrame(runDragAutoScroll);
    } else if (!x && !y) {
      stopDragAutoScroll();
    }
  }, [runDragAutoScroll, stopDragAutoScroll]);

  useEffect(() => stopDragAutoScroll, [stopDragAutoScroll]);

  const handleTankDragStart = useCallback((event, tankKey) => {
    if (!tankKey) return;
    const { dataTransfer } = event;
    setDraggedTankKey(tankKey);
    dataTransfer.effectAllowed = 'move';
    dataTransfer.setData('text/plain', tankKey);
  }, []);

  const handleTankDragOver = useCallback((event, tankKey) => {
    if (!draggedTankKey) return;
    const { dataTransfer } = event;
    event.preventDefault();
    handleDragAutoScroll(event);
    if (draggedTankKey === tankKey) return;
    dataTransfer.dropEffect = 'move';
    setDragOverTankKey(tankKey);
  }, [draggedTankKey, handleDragAutoScroll]);

  const handleTankDrop = useCallback((event, targetTankKey) => {
    event.preventDefault();
    const sourceTankKey = event.dataTransfer.getData('text/plain') || draggedTankKey;
    setDraggedTankKey('');
    setDragOverTankKey('');
    stopDragAutoScroll();
    suppressClickAfterDrag();

    if (!sourceTankKey || !targetTankKey || sourceTankKey === targetTankKey) return;

    const nextOrder = orderedTanks.map(getTankIdentifier).filter(Boolean);
    const sourceIndex = nextOrder.indexOf(sourceTankKey);
    const targetIndex = nextOrder.indexOf(targetTankKey);
    if (sourceIndex === -1 || targetIndex === -1) return;

    nextOrder.splice(sourceIndex, 1);
    nextOrder.splice(targetIndex, 0, sourceTankKey);
    setTankOrder(nextOrder);
    storeTankOrder(nextOrder);
  }, [draggedTankKey, orderedTanks, stopDragAutoScroll, suppressClickAfterDrag]);

  const handleTankDragEnd = useCallback(() => {
    if (draggedTankKey) suppressClickAfterDrag();
    setDraggedTankKey('');
    setDragOverTankKey('');
    stopDragAutoScroll();
  }, [draggedTankKey, stopDragAutoScroll, suppressClickAfterDrag]);

  if (loading && page === 1) {
    return <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3 }} />;
  }

  const renderContent = () => {
    if (!tanks || tanks.length === 0) {
      return (
        <Box
          sx={{
            py: 6,
            px: 2,
            textAlign: 'center',
            color: 'text.secondary'
          }}
        >
          <Typography variant="body2">
            No tanks found for this site.
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Stack spacing={2} direction={compact ? 'column' : 'row'}>
          {compact
            ? orderedTanks.map((tank) => {
              const tankKey = getTankIdentifier(tank);
              return (
                <Box
                  key={tankKey || tank.id}
                  draggable={Boolean(tankKey)}
                  onClickCapture={(event) => {
                    if (!suppressNextClickRef.current) return;
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onDragStart={(event) => handleTankDragStart(event, tankKey)}
                  onDragOver={(event) => handleTankDragOver(event, tankKey)}
                  onDragLeave={() => setDragOverTankKey((currentKey) => (currentKey === tankKey ? '' : currentKey))}
                  onDrop={(event) => handleTankDrop(event, tankKey)}
                  onDragEnd={handleTankDragEnd}
                  sx={{
                    cursor: tankKey ? 'grab' : 'default',
                    opacity: draggedTankKey === tankKey ? 0.55 : 1,
                    transform: dragOverTankKey === tankKey ? 'translateY(3px)' : 'none',
                    transition: 'opacity 160ms ease, transform 160ms ease',
                    '&:active': {
                      cursor: tankKey ? 'grabbing' : 'default'
                    }
                  }}
                >
                  <TankReconciliationTooltip
                    reconciliation={getTankReconciliation(tank, reconciliationReports)}
                    currentStock={(tank.currentBalance ?? tank.current_balance)?.stock}
                    currentStockGallons={(tank.currentBalance ?? tank.current_balance)?.stock_gallons}
                  >
                    <TankCard tank={tank} />
                  </TankReconciliationTooltip>
                </Box>
              );
            })
            : (
              <Box
                ref={scrollContainerRef}
                sx={{
                  display: 'flex',
                  gap: 3,
                  overflowX: 'auto',
                  pb: 2,
                  mx: -1,
                  px: 1,
                  width: '100%'
                }}
              >
                {orderedTanks.map((tank) => {
                  const tankKey = getTankIdentifier(tank);
                  return (
                    <Box
                      key={tankKey || tank.id}
                      draggable={Boolean(tankKey)}
                      onClickCapture={(event) => {
                        if (!suppressNextClickRef.current) return;
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onDragStart={(event) => handleTankDragStart(event, tankKey)}
                      onDragOver={(event) => handleTankDragOver(event, tankKey)}
                      onDragLeave={() => setDragOverTankKey((currentKey) => (currentKey === tankKey ? '' : currentKey))}
                      onDrop={(event) => handleTankDrop(event, tankKey)}
                      onDragEnd={handleTankDragEnd}
                      sx={{
                        minWidth: { xs: '100%', sm: 300, md: 0 },
                        width: { xs: '100%', sm: 300, md: '100%' },
                        maxWidth: '100%',
                        flexShrink: 0,
                        cursor: tankKey ? 'grab' : 'default',
                        opacity: draggedTankKey === tankKey ? 0.55 : 1,
                        transform: dragOverTankKey === tankKey ? 'translateX(8px)' : 'none',
                        transition: 'opacity 160ms ease, transform 160ms ease',
                        '&:active': {
                          cursor: tankKey ? 'grabbing' : 'default'
                        }
                      }}
                    >
                      <TankReconciliationTooltip
                        reconciliation={getTankReconciliation(tank, reconciliationReports)}
                        currentStock={(tank.currentBalance ?? tank.current_balance)?.stock}
                        currentStockGallons={(tank.currentBalance ?? tank.current_balance)?.stock_gallons}
                      >
                        <TankCard tank={tank} />
                      </TankReconciliationTooltip>
                    </Box>
                  );
                })}
              </Box>
            )}
        </Stack>

        {/* Load More Button */}
        {hasMore && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? (<CircularProgress size={14} />) : (<AddIcon />)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              borderColor: alpha(theme.palette.primary.main, 0.3),
              '&:hover': {
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.05)
              }
            }}
          >
            {loadingMore ? 'Loading...' : 'Load More Tanks'}
          </Button>
        </Box>
        )}
      </>
    );
  };

  if (compact) {
    return (
      <Card
        sx={{
          p: 0,
          borderRadius: 3,
          height: '100%',
          minHeight: { xs: 420, md: 500 },
          maxHeight: { xs: 'none', md: 575 },
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.06)'
        }}
      >
        <Box sx={{ p: { xs: 1.75, sm: 2.25 }, pb: 1.5, borderBottom: '1px solid #f0f0f0' }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1.5}
            flexWrap="wrap"
            columnGap={2}
            rowGap={1.25}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ minWidth: 0, flex: '1 1 220px' }}
            >
              <Box
                sx={{
                  p: 0.8,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  color: theme.palette.info.main,
                  display: 'flex'
                }}
              >
                <OpacityIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                <Typography
                  variant="h6"
                  fontWeight={800}
                  lineHeight={1.2}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  LIVE TANK LEVELS
                </Typography>
                <Box sx={{ minWidth: 0 }}>{renderStockSummary()}</Box>
              </Box>
            </Stack>
            <Box
              sx={{
                alignSelf: 'center',
                minWidth: 0,
                flex: '0 0 auto',
                maxWidth: '100%'
              }}
            >
              {renderHeaderActions()}
            </Box>
          </Stack>
        </Box>

        <Box
          ref={scrollContainerRef}
          sx={{
            p: { xs: 1.25, sm: 1.75 },
            overflowY: 'auto',
            flexGrow: 1,
            minHeight: 0,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.divider, 0.5),
              borderRadius: 4
            }
          }}
        >
          {renderContent()}
        </Box>
      </Card>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={{
          mb: 2,
          px: 1
        }}
      >
        <Box
          sx={{
            p: 1,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.info.main, 0.1),
            color: theme.palette.info.main,
            display: 'flex'
          }}
        >
          <OpacityIcon fontSize="small" />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            LIVE TANK LEVELS
          </Typography>
          {renderStockSummary()}
        </Box>
        {renderHeaderActions()}
      </Stack>

      {renderContent()}
    </Box>
  );
};

StockInfo.propTypes = {
  compact: PropTypes.bool
};

StockInfo.defaultProps = {
  compact: false
};

export default StockInfo;
